import React, { useState, useEffect, useContext } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import * as api from '@/apis/aipass'
import {
  SearchReservationTypeWhenCheckedIn,
  SearchReservationTypeWhenNonCheckIn,
  SelfCheckInReservationSearchQuery,
} from '@/models/reservation'
import { LoaderContextCreator } from '@/contexts/loader'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { PaymentTerminalSettingType, TerminalPaymentScene, PaymentMethodType } from '@/models/payment-setting'
import { hasAnyReservationPlugin } from '@/libs/plugins'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { AxiosError } from 'axios'
import { reloadIfVersionHasChanged } from '@/utils/force-version-up'
import { signout } from '@/apis/auth0'
import { formSchema, FormType, ReservationSearchForm } from '@/components/molecules/front-tablet/reservation-search-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { FrontTabletInputField } from '@/components/molecules/front-tablet/input/input-field'
import { SearchItem, SearchSetting } from '@/models/search-setting'
import { reportError } from '@/components/apps/error-boundary'
import { notification } from 'antd'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'

export const SearchReservation: React.FC<{}> = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const history = useHistory()
  const locateInfo = useLocation<{ hotelId: string }>()
  const { state, saveState, initializeState } = useSelfCheckInState()
  const formReturn = useForm<FormType>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: state.searchReservation?.condition,
  })
  const watchSearchMode = useWatch({ control: formReturn.control, name: 'mode' })

  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const [isDuplicateReservationBySameName, setIsDuplicateReservationBySameName] = useState(false)
  const [searchItems, setSearchItems] = useState<SearchSetting[]>([])

  const hasDuplicateReservationBySameName = (
    searchedReservations: SearchReservationTypeWhenNonCheckIn[] | SearchReservationTypeWhenCheckedIn[] | undefined,
  ): boolean => {
    if (!searchedReservations || searchedReservations.length === 0) return false
    return searchedReservations.some((searchedReservation, _, reservations) => {
      return reservations.some(
        reservation =>
          (reservation.guestName === searchedReservation.guestName ||
            reservation.userName === searchedReservation.userName ||
            reservation.guestNameKana === searchedReservation.guestNameKana ||
            reservation.userNameKana === searchedReservation.userNameKana) &&
          reservation.guestTel !== searchedReservation.guestTel,
      )
    })
  }

  // 初期表示時にQRが選択されている場合
  // 「次へ」ボタンが押せて欲しいが、isValidがfalseのため、disableになっている
  // isValidは、バリデーションが完了してからtrueになるため。
  // そのため、QRが選択された場合は、バリデーションをトリガーしてあげる必要がある
  useEffect(() => {
    if (watchSearchMode === 'qr') {
      formReturn.trigger()
    }
  }, [watchSearchMode, formReturn])

  const submit = async (formValues: FormType) => {
    if (formValues.mode === 'qr') {
      saveState({
        ...state,
        searchReservation: {
          condition: formValues,
          result: {
            isDuplicateReservationBySameName: false,
            notCheckinReservations: undefined,
            preCheckinReservations: undefined,
          },
        },
        selectReservation: undefined,
      })
      history.push({ pathname: '/self-checkin/qr-checkin-camera' })
      return
    }

    setIsLoading(true)
    // APIには、選択されたmodeの値しか送らない
    const params: SelfCheckInReservationSearchQuery = {
      hotelId: state.hotelId,
      reservationTel: isDuplicateReservationBySameName
        ? phoneNumber
        : formValues.mode === SearchItem.Telephone
          ? formValues.tel
          : undefined,
      reservationId: formValues.mode === SearchItem.ReservationId ? formValues.reservationId : undefined,
      guestName:
        formValues.mode === SearchItem.GuestName && formValues.firstName && formValues.lastName
          ? `${formValues.firstName} ${formValues.lastName}`
          : undefined,
      guestNameKana:
        formValues.mode === SearchItem.GuestNameKana && formValues.firstNameKana && formValues.lastNameKana
          ? `${formValues.firstNameKana} ${formValues.lastNameKana}`
          : undefined,
      isCheckin: 0,
    }

    const availableError: AxiosError[] = []
    const [notCheckinReservationFetchResponse, preCheckinReservationFetchResponse] = await Promise.all([
      api.fetchReservationsForSelfCheckin({ ...params }).catch(e => {
        if (e.response?.status === 403) {
          availableError.push(e)
        }
      }),
      api.fetchReservationsForSelfCheckin({ ...params, isCheckin: 1 }).catch(e => {
        if (e.response?.status === 403) {
          availableError.push(e)
        }
      }),
    ])
    setIsLoading(false)

    let noticeMessage = t('Reservation not found. Please try a different search method.')
    if (!notCheckinReservationFetchResponse && !preCheckinReservationFetchResponse) {
      if (availableError[0]?.response) {
        noticeMessage = t(availableError[0].response.data.errorMessage, availableError[0].response.data.messageParameter)
      }
      notification.warn({ message: noticeMessage, style: { whiteSpace: 'pre-line' } })
      return
    }
    const notCheckinReservations = notCheckinReservationFetchResponse as SearchReservationTypeWhenNonCheckIn[] | undefined
    const preCheckinReservations = preCheckinReservationFetchResponse as SearchReservationTypeWhenCheckedIn[] | undefined

    if (
      !isDuplicateReservationBySameName &&
      formValues.mode !== 'telephone' &&
      (hasDuplicateReservationBySameName(notCheckinReservations) || hasDuplicateReservationBySameName(preCheckinReservations))
    ) {
      setIsDuplicateReservationBySameName(true)
      return
    }

    saveState({
      ...state,
      searchReservation: {
        condition: formValues,
        result: {
          isDuplicateReservationBySameName: false,
          notCheckinReservations,
          preCheckinReservations,
        },
      },
      selectReservation: undefined,
    })
    history.push({ pathname: '/self-checkin/select-reservation' })
  }

  const fetchPaymentSetting = async (): Promise<{
    terminal: PaymentTerminalSettingType
    manual: Array<PaymentMethodType>
  }> => {
    return api.fetchPaymentSettingByHotelId(state.hotelId).then(res => {
      const terminal: PaymentTerminalSettingType = {
        isEnabledCash: res?.terminals?.isEnabledCash ?? true,
        isEnabledQr: res?.terminals?.isEnabledQr ?? false,
        terminalPaymentScene: res?.terminals?.terminalPaymentScene ?? TerminalPaymentScene.Both,
      }

      const selfCheckinPaymentSetting = res?.settings?.payment_time?.find(item => item.value === 'SELF_CHECKIN')
      if (!selfCheckinPaymentSetting?.is_enabled) {
        return { terminal, manual: [] }
      }
      const temp: PaymentMethodType[] = []
      for (let index = 0; index < selfCheckinPaymentSetting.payment_method.length; index++) {
        const paymentMethod = selfCheckinPaymentSetting.payment_method[index]
        if (paymentMethod.is_enabled) {
          temp.push(paymentMethod.value)
        }
      }
      return {
        terminal,
        manual: temp,
      }
    })
  }

  const initializeHotelInfo = async () => {
    const [{ results }, selfCheckinSetting, paymentSetting, customCheckin] = await Promise.all([
      api.fetchFacilityBasicInfoInSelfCheckin(state.hotelId),
      api.fetchSelfCheckinSetting(state.hotelId),
      fetchPaymentSetting(),
      api.fetchGuide(state.hotelId).then(res => res?.customCheckin || []),
    ])

    const initializedState = initializeState(state.hotelId, {
      basicInfo: results,
      customCheckin,
      payment: paymentSetting.manual,
      paymentTerminal: paymentSetting.terminal,
      selfCheckinSetting,
    })
    saveState(initializedState)
    setSearchItems(selfCheckinSetting?.searchItems.filter(item => item.isEnabled) || [])
    return initializedState
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (reloadIfVersionHasChanged()) {
      return
    }
    if (!state?.hotelId) {
      const locateHotelId = locateInfo.state?.hotelId || sessionStorage.getItem('selfci.hotelId')
      if (locateHotelId) {
        history.replace({ pathname: '/self-checkin', search: `?hotelId=${locateHotelId}` })
        return
      }
      signout()
      history.push({ pathname: '/dashboard' })
      return
    }

    setIsLoading(true)
    initializeHotelInfo()
      .then(_state => {
        if (!hasAnyReservationPlugin(_state.setting.plugin)) {
          history.replace('/self-checkin/accommodation-info')
          return
        }
        setIsInitializeComponent(true)
      })
      .catch(e => reportError(e))
      .finally(() => setIsLoading(false))
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <SelfCheckinLayout
      state={state}
      header={{ goToPreviousPage: history.goBack, title: t('Search your reservation') }}
      subtitle={{
        title: t(
          isDuplicateReservationBySameName
            ? 'Found a reservation with the same name.\nPlease enter your phone number.'
            : 'Please search by one of the following methods',
        ),
      }}
      footer={{
        right: {
          isDisabled: isDuplicateReservationBySameName ? !phoneNumber.trim() : !formReturn.formState.isValid,
          onClick: formReturn.handleSubmit(submit),
          label: t(watchSearchMode === 'qr' ? 'Next' : 'Search'),
        },
      }}
    >
      <div css={form}>
        {isDuplicateReservationBySameName ? (
          <div className="duplicate-form-block">
            <p className="field-title">{t('Phone number')}</p>
            <div className="text-field">
              <FrontTabletInputField
                name="phoneNumber"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                type="tel"
                placeholder="08012345678"
              />
            </div>
          </div>
        ) : (
          <ReservationSearchForm formReturn={formReturn} searchItems={searchItems} />
        )}
      </div>
    </SelfCheckinLayout>
  )
}

const form = css(
  {
    backgroundColor: 'white',
    borderRadius: '10px',
    '.duplicate-form-block': {
      padding: '18px 32px',
      '.field-title': { fontWeight: 'bold', fontSize: 20 },
      '.text-field': { marginTop: 16, marginLeft: 43 },
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      borderRadius: 0,
      '.duplicate-form-block': {
        '.text-field': { marginLeft: 0 },
      },
    },
    landscape13: {
      '.duplicate-form-block': {
        padding: '24px 32px',
        '.field-title': { fontSize: FrontTabletResponsiveToken.formLabelFontSize.landscape13 },
      },
    },
  }),
)
